import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where
} from 'firebase/firestore';
import { buildFactionMemberFields, getFactionStoredWrestlers } from './factionWrestlers';
import {
  applyFactionMemberRoleToPoints,
  hasFactionScoringStarted,
  shouldBlockFactionScoringForMissingLineup,
  shouldZeroFactionMemberPointsForMissingLineupRole
} from './factionScoring';
import {
  getFactionMemberRoleAtDateFromLineup
} from './factionLineupRole';

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getShowScoringDate = (showData) =>
  toDate(showData?.data || showData?.date || showData?.timestamp) || new Date();

export const getFactionMemberRoleAtDate = (faction, wrestlerId, referenceDate) => {
  return getFactionMemberRoleAtDateFromLineup(faction, wrestlerId, referenceDate);
};

const toFiniteNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

export const buildFactionMemberScoreUpdateForParticipation = ({
  faction,
  roster = null,
  wrestlerId,
  rawPointsDelta,
  showDate
}) => {
  const rawDelta = toFiniteNumber(rawPointsDelta);
  if (!faction || !wrestlerId || !rawDelta) return null;

  const members = getFactionStoredWrestlers(faction);
  if (!members.some((member) => member.id === wrestlerId)) return null;

  const scoringStarted = hasFactionScoringStarted(faction, showDate, roster);

  if (!scoringStarted) {
    return {
      members: members.map((member) =>
        member.id === wrestlerId
          ? {
              ...member,
              startScore: toFiniteNumber(member.startScore) + rawDelta,
              currentScore:
                toFiniteNumber(
                  member.currentScore ?? member.score ?? member.startScore
                ) + rawDelta
            }
          : member
      ),
      accumulatedPointsDelta: 0
    };
  }

  if (shouldBlockFactionScoringForMissingLineup({ faction, roster, referenceDate: showDate })) {
    return null;
  }

  const roleAtShow = getFactionMemberRoleAtDate(faction, wrestlerId, showDate);
  const scoredDelta = shouldZeroFactionMemberPointsForMissingLineupRole({
    faction,
    roster,
    wrestlerId,
    effectiveStatus: roleAtShow
  })
    ? 0
    : applyFactionMemberRoleToPoints(rawDelta, roleAtShow);

  return {
    members: members.map((member) =>
      member.id === wrestlerId
        ? {
            ...member,
            startScore: toFiniteNumber(member.startScore) + rawDelta,
            currentScore:
              toFiniteNumber(
                member.currentScore ?? member.score ?? member.startScore
              ) + rawDelta,
            accumulatedPoints:
              toFiniteNumber(member.accumulatedPoints) + scoredDelta
          }
        : member
    ),
    accumulatedPointsDelta: scoredDelta
  };
};

export const shouldFactionReceiveWrestlerScoreUpdate = (faction, wrestlerId) =>
  Boolean(wrestlerId) &&
  getFactionStoredWrestlers(faction).some((member) => member.id === wrestlerId);

const getFactionDocsForWrestlerScoreUpdate = async (db, wrestlerId) => {
  const indexedSnap = await getDocs(
    query(collection(db, 'factions'), where('wrestlerIds', 'array-contains', wrestlerId))
  );
  const docsById = new Map(indexedSnap.docs.map((factionDoc) => [factionDoc.id, factionDoc]));

  const allFactionsSnap = await getDocs(collection(db, 'factions'));
  allFactionsSnap.docs.forEach((factionDoc) => {
    if (docsById.has(factionDoc.id)) return;

    const faction = { id: factionDoc.id, ...factionDoc.data() };
    if (shouldFactionReceiveWrestlerScoreUpdate(faction, wrestlerId)) {
      docsById.set(factionDoc.id, factionDoc);
    }
  });

  return Array.from(docsById.values());
};

export const applyParticipationDeltaToFactionScores = async ({
  db,
  wrestlerId,
  rawPointsDelta,
  showDate
}) => {
  if (!wrestlerId || !rawPointsDelta) return;

  const factionDocs = await getFactionDocsForWrestlerScoreUpdate(db, wrestlerId);

  await Promise.all(
    factionDocs.map(async (factionDoc) => {
      const faction = { id: factionDoc.id, ...factionDoc.data() };
      let roster = null;

      if (faction.rosterId) {
        const rosterSnap = await getDoc(doc(db, 'rosters', faction.rosterId));
        roster = rosterSnap.exists() ? { id: rosterSnap.id, ...rosterSnap.data() } : null;
      }

      const scoreUpdate = buildFactionMemberScoreUpdateForParticipation({
        faction,
        roster,
        wrestlerId,
        rawPointsDelta,
        showDate
      });

      if (!scoreUpdate) return;

      await updateDoc(doc(db, 'factions', factionDoc.id), {
        ...buildFactionMemberFields(scoreUpdate.members),
        accumulatedPoints:
          (faction.accumulatedPoints || 0) + scoreUpdate.accumulatedPointsDelta
      });
    })
  );
};
